import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { getEntry } from "@/components/Checkout/actions";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import Modal from "@/components/ui/Modal/Modal";
import ModalContent from "@/components/ui/Modal/ModalContent";
import { ISingleColumnRichText } from "@/types";

export default function TermsAndPrivacy({ translationKey, genericLeadForm }: { translationKey: string; genericLeadForm?: boolean }) {
  const t = useTranslations();
  const [openTerms, setOpenTerms] = useState(false);
  const [termsEntry, setTermsEntry] = useState<ISingleColumnRichText["entry"]>();
  const [openPrivacy, setOpenPrivacy] = useState(false);
  const [privacyEntry, setPrivacyEntry] = useState<ISingleColumnRichText["entry"]>();

  // fetch privacy
  useEffect(() => {
    const fetchData = async () => {
      try {
        const privacy = await getEntry(process.env.NEXT_PUBLIC_PRIVACY_POLICY || "");

        setPrivacyEntry(privacy);
      } catch (error) {
        console.error("Errore nel fetch:", error);
      }
    };

    if (!privacyEntry) {
      fetchData();
    }
  }, [openPrivacy]);

  // fetch privacy/terms and conditions
  useEffect(() => {
    const fetchData = async () => {
      try {
        let terms;
        if (genericLeadForm) {
          terms = await getEntry(process.env.NEXT_PUBLIC_GEN_TNC || "");
        } else {
          terms = await getEntry(process.env.NEXT_PUBLIC_TERMS_AND_CONDITIONS || "");
        }
        setTermsEntry(terms);
      } catch (error) {
        console.error("Errore nel fetch:", error);
      }
    };

    if (!termsEntry) {
      fetchData();
    }
  }, [openTerms]);

  return t.rich(translationKey, {
    terms: (chunks) => (
      <>
        <button className="font-medium underline" onClick={() => setOpenTerms(true)} type="button">
          {chunks}
        </button>
        <Modal open={openTerms && !!termsEntry} onClose={() => setOpenTerms(false)}>
          <ModalContent className="py-l !rounded-none md:px-l md:!rounded-[8px] md:w-12\/24 md:h-auto md:max-h-[70vh] h-svh">
            <>
              <div className="flex justify-between pb-l">
                <h2 className="font-medium uppercase font-primary text-m ">{termsEntry?.title}</h2>
                <Button
                  className="modal__close"
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.TERTIARY}
                  theme={ButtonTheme.BLACK}
                  onClick={() => setOpenTerms(false)}
                  type="button"
                >
                  <span className="pointer-events-none">
                    <Icon name="close" size="24" title="close" className="stroke-black" />
                    <span className="sr-only">{t("button.label.closeModal")}</span>
                  </span>
                </Button>
              </div>
              {termsEntry?.description && <div dangerouslySetInnerHTML={{ __html: termsEntry.description }} />}
            </>
          </ModalContent>
        </Modal>
      </>
    ),
    privacy: (chunks) => (
      <>
        <button className="font-medium underline" onClick={() => setOpenPrivacy(true)} type="button">
          {chunks}
        </button>
        <Modal open={openPrivacy && !!privacyEntry} onClose={() => setOpenPrivacy(false)}>
          <ModalContent className="py-l !rounded-none md:px-l md:!rounded-[8px] md:w-12\/24 md:h-auto md:max-h-[70vh] h-svh">
            <>
              <div className="flex justify-between pb-l">
                <h2 className="font-medium uppercase font-primary text-m ">{privacyEntry?.title}</h2>
                <Button
                  className="modal__close"
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.TERTIARY}
                  theme={ButtonTheme.BLACK}
                  onClick={() => setOpenPrivacy(false)}
                  type="button"
                >
                  <span className="pointer-events-none">
                    <Icon name="close" size="24" title="close" className="stroke-black" />
                    <span className="sr-only">{t("button.label.close")}</span>
                  </span>
                </Button>
              </div>
              {privacyEntry?.description && <div dangerouslySetInnerHTML={{ __html: privacyEntry.description }} />}
            </>
          </ModalContent>
        </Modal>
      </>
    ),
  });
}
